import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  type FilterOptionsState,
  TextField,
} from "@mui/material";
import { classes } from "portal/theme/theme";
import { CustomerResponse } from "protos/portal/customers";
import { formatList, normalizeAccentsAndCase } from "portal/utils/strings";
import { getAutocompleteText } from "portal/utils/forms";
import { portalApi } from "portal/state/portalApi";
import { portalLanguageOrDefault } from "portal/i18nConstants";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { WithSkeleton } from "../WithSkeleton";
import React, { FC, useMemo, useState } from "react";

interface BaseProps {
  readOnly?: boolean;
  emptyText?: string;
  small?: boolean;
  className?: string;
}
interface SingleIdProps extends BaseProps {
  valueKey?: "id"; // id for backwards compatibility -- but we should try to switch to 'uuid'
  value?: number | undefined;
  multiple?: false | undefined;
  deferClearUntilBlur?: boolean;
  onChange: (customerId: number | undefined) => void;
}

interface MultipleIdProps extends BaseProps {
  valueKey?: "id";
  value?: number[] | undefined;
  multiple: true;
  deferClearUntilBlur?: never;
  onChange: (customerIds: number[] | undefined) => void;
}

interface SingleUuidProps extends BaseProps {
  valueKey: "uuid";
  value?: string | undefined;
  multiple?: false | undefined;
  deferClearUntilBlur?: boolean;
  onChange: (customerUuid: string | undefined) => void;
}

interface MultipleUuidProps extends BaseProps {
  valueKey: "uuid";
  value?: string[] | undefined;
  multiple: true;
  deferClearUntilBlur?: never;
  onChange: (customerUuids: string[] | undefined) => void;
}

type CustomerSelectorProps =
  | SingleIdProps
  | MultipleIdProps
  | SingleUuidProps
  | MultipleUuidProps;

type CustomerValueKey = "id" | "uuid";

const customerToSelectValue = (
  customer: CustomerResponse,
  valueKey: CustomerValueKey
): string | undefined => {
  if (valueKey === "uuid") {
    return customer.uuid || undefined;
  }
  const id = customer.db?.id;
  return id === undefined ? undefined : String(id);
};

const filterCustomers = (
  options: CustomerResponse[],
  state: FilterOptionsState<CustomerResponse>
): CustomerResponse[] =>
  options.filter((customer) =>
    normalizeAccentsAndCase(customer.name).includes(
      normalizeAccentsAndCase(state.inputValue)
    )
  );

const idsFromCustomers = (customers: CustomerResponse[]): number[] =>
  customers.flatMap((customer) => {
    const id = customer.db?.id;
    return id === undefined ? [] : [id];
  });

export const CustomerSelector: FC<CustomerSelectorProps> = (props) => {
  const {
    readOnly = false,
    emptyText,
    multiple,
    small = false,
    className,
    valueKey = "id",
    deferClearUntilBlur = false,
  } = props;
  const {
    data: allCustomers,
    isSuccess,
    isLoading,
  } = useQueryPopups(portalApi.endpoints.listCustomers.useQuery());
  const { t, i18n } = useTranslation();
  const locale = portalLanguageOrDefault(i18n.language);

  const options = useMemo(() => {
    return (
      allCustomers
        ?.filter(
          (customer) => customerToSelectValue(customer, valueKey) !== undefined
        )
        .toSorted((c1, c2) => c1.name.localeCompare(c2.name, locale)) ?? []
    );
  }, [allCustomers, locale, valueKey]);

  const rawValue = props.value;
  const selectedValues = useMemo((): string[] => {
    if (Array.isArray(rawValue)) {
      return rawValue.map(String);
    }
    if (rawValue === undefined || rawValue === "") {
      return [];
    }
    return [String(rawValue)];
  }, [rawValue]);
  const selectedCustomers = useMemo(
    () =>
      options.filter((customer) => {
        const customerValue = customerToSelectValue(customer, valueKey);
        return (
          customerValue !== undefined && selectedValues.includes(customerValue)
        );
      }),
    [options, selectedValues, valueKey]
  );

  const [clearedValue, setClearedValue] = useState<
    string | number | undefined
  >();
  const pendingClear =
    deferClearUntilBlur &&
    !Array.isArray(rawValue) &&
    clearedValue !== undefined &&
    clearedValue === rawValue;

  const label = multiple
    ? t("models.customers.customer_title_other")
    : t("models.customers.customer_title_one");

  if (readOnly) {
    return (
      <span>
        {formatList(
          t,
          selectedCustomers.map(({ name }) => name)
        ) || t("components.customers.CustomerSelector.empty")}
      </span>
    );
  }

  const placeholder =
    emptyText ?? t("components.customers.CustomerSelector.empty");
  const renderInput = (
    params: AutocompleteRenderInputParams
  ): React.JSX.Element => (
    <TextField
      {...params}
      size={small ? "small" : "medium"}
      label={label}
      placeholder={selectedCustomers.length === 0 ? placeholder : undefined}
      slotProps={{
        ...params.slotProps,
        inputLabel: {
          ...(typeof params.slotProps.inputLabel === "function"
            ? {}
            : params.slotProps.inputLabel),
          shrink: true,
        },
      }}
    />
  );
  const autocompleteClassName = classes(
    "mt-1 w-full text-inherit",
    "[&_.MuiOutlinedInput-notchedOutline]:border-current hover:[&_.MuiOutlinedInput-notchedOutline]:border-current",
    "[&_.Mui-focused_.MuiOutlinedInput-notchedOutline]:border-current",
    "[&_.MuiAutocomplete-popupIndicator]:text-inherit [&_.MuiAutocomplete-clearIndicator]:text-inherit",
    "[&_.MuiInputBase-root]:text-inherit [&_.MuiInputBase-input]:text-inherit [&_.MuiInputLabel-root]:text-inherit",
    className
  );

  if (props.multiple) {
    return (
      <WithSkeleton
        variant="rectangular"
        success={isSuccess}
        className={autocompleteClassName}
      >
        <Autocomplete<CustomerResponse, true, false, false>
          {...getAutocompleteText(t)}
          openOnFocus
          multiple
          disableCloseOnSelect
          fullWidth
          size={small ? "small" : "medium"}
          loading={isLoading}
          options={options}
          value={selectedCustomers}
          getOptionLabel={(customer) => customer.name}
          isOptionEqualToValue={(option, value) =>
            customerToSelectValue(option, valueKey) ===
            customerToSelectValue(value, valueKey)
          }
          onChange={(_event, next) => {
            if (props.valueKey === "uuid") {
              props.onChange(next.map((customer) => customer.uuid));
              return;
            }
            props.onChange(idsFromCustomers(next));
          }}
          filterOptions={filterCustomers}
          className={autocompleteClassName}
          renderInput={renderInput}
        />
      </WithSkeleton>
    );
  }

  return (
    <WithSkeleton
      variant="rectangular"
      success={isSuccess}
      className={autocompleteClassName}
    >
      <Autocomplete<CustomerResponse, false, false, false>
        {...getAutocompleteText(t)}
        openOnFocus
        fullWidth
        size={small ? "small" : "medium"}
        loading={isLoading}
        options={options}
        value={
          (pendingClear ? undefined : selectedCustomers[0]) ??
          // eslint-disable-next-line unicorn/no-null -- MUI Autocomplete uses null for an empty controlled value
          null
        }
        getOptionLabel={(customer) => customer.name}
        isOptionEqualToValue={(option, value) =>
          customerToSelectValue(option, valueKey) ===
          customerToSelectValue(value, valueKey)
        }
        onBlur={() => {
          if (!pendingClear) {
            return;
          }
          setClearedValue(undefined);
          props.onChange(undefined);
        }}
        onChange={(_event, next) => {
          if (!next && deferClearUntilBlur && !Array.isArray(rawValue)) {
            setClearedValue(rawValue);
            return;
          }
          setClearedValue(undefined);
          if (props.valueKey === "uuid") {
            props.onChange(next?.uuid || undefined);
            return;
          }
          props.onChange(next?.db?.id);
        }}
        filterOptions={filterCustomers}
        className={autocompleteClassName}
        renderInput={renderInput}
      />
    </WithSkeleton>
  );
};
